/**
 * Sub-tab "Clientes" — comparativo de utilidad por cliente entre dos años.
 * Layout: full-width desktop. Tabla con sticky header, búsqueda y scroll.
 */
import React, { useEffect, useState } from 'react';
import { Select, Card } from '../../../../../shared/components';
import { ComparativosMultiSelect } from './ComparativosMultiSelect';
import { getComparativosRepository } from '../../../../../shared/services/results/comparativosRepository';
import { getResultsRepository } from '../../../../../shared/services/results/repositoryFactory';
import { ClientYearComparisonRowDto } from '../../../../../shared/services/results/types.comparativos';
import { ServiceType } from '../../../../../shared/services/results/types';
import { ClientsYearChart } from './ClientsYearChart';
import { money, pctFmt, varColor, yearOptions } from './comparativosUtils';
import { exportClientsYearExcel } from '../../../utils/exportComparativos';

const repo = getComparativosRepository();
const baseRepo = getResultsRepository();

export function ClientsYearTab() {
  const now = new Date();
  const [anio1, setAnio1] = useState(now.getFullYear() - 1);
  const [anio2, setAnio2] = useState(now.getFullYear());
  const [tiposServicio, setTiposServicio] = useState<string[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [data, setData] = useState<ClientYearComparisonRowDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortField, setSortField] = useState<'utilidadAnio2' | 'varUtilidadAbs' | 'varUtilidadPct'>('utilidadAnio2');
  const [search, setSearch] = useState('');

  useEffect(() => {
    baseRepo.listServiceTypes().then(setServiceTypes);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    repo
      .getClientYearComparison({ anio1, anio2, tiposServicio: tiposServicio.length ? tiposServicio : undefined })
      .then((d: ClientYearComparisonRowDto[]) => { setData(d); setIsLoading(false); })
      .catch((err) => { console.error('[ClientsYearTab]', err); setIsLoading(false); });
  }, [anio1, anio2, tiposServicio]);

  const yOpts = yearOptions(now.getFullYear());

  const sorted = [...data].sort((a, b) => {
    if (sortField === 'utilidadAnio2') return b.utilidadAnio2 - a.utilidadAnio2;
    if (sortField === 'varUtilidadAbs') return Math.abs(b.varUtilidadAbs) - Math.abs(a.varUtilidadAbs);
    const bv = b.varUtilidadPct ?? -Infinity;
    const av = a.varUtilidadPct ?? -Infinity;
    return bv - av;
  });

  const q = search.trim().toLowerCase();
  const displayed = q ? sorted.filter((r) => r.cliente.toLowerCase().includes(q)) : sorted;

  return (
    <div className="relative left-1/2 -ml-[50vw] w-screen px-4 sm:px-6 lg:px-8">
      <div className="space-y-4">
        {/* Filtros */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:max-w-2xl">
          <Select label="Año 1 (base)" options={yOpts} value={String(anio1)} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAnio1(Number(e.target.value))} />
          <Select label="Año 2 (comparar)" options={yOpts} value={String(anio2)} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAnio2(Number(e.target.value))} />
          <ComparativosMultiSelect label="Tipo Servicio" options={serviceTypes.map((s) => s.nombre)} selected={tiposServicio} onApply={setTiposServicio} />
        </div>

        {/* Botón Excel */}
        {data.length > 0 && (
          <div className="flex justify-end">
            <button
              onClick={() => exportClientsYearExcel(data, anio1, anio2)}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 active:scale-95"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              Descargar Excel
            </button>
          </div>
        )}

        {isLoading && <p className="py-8 text-center text-sm text-ink-400">Cargando comparativo clientes...</p>}

        {!isLoading && data.length > 0 && (
          <>
            <Card>
              <ClientsYearChart rows={sorted} anio1={anio1} anio2={anio2} topN={15} />
            </Card>

            {/* Controles tabla: ordenar + buscar */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1.5 text-xs text-ink-500">
                <span className="font-medium">Ordenar por:</span>
                {([
                  { key: 'utilidadAnio2', label: `Utilidad ${anio2}` },
                  { key: 'varUtilidadAbs', label: 'Mayor variación $' },
                  { key: 'varUtilidadPct', label: 'Mayor variación %' },
                ] as const).map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortField(opt.key)}
                    className={`rounded-full px-3 py-1 font-medium transition-colors ${sortField === opt.key ? 'bg-brand-600 text-white' : 'bg-surface-2 text-ink-600 hover:bg-surface-3'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Búsqueda */}
              <div className="relative ml-auto">
                <svg className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Buscar cliente..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="rounded-lg border border-border bg-surface py-1.5 pl-8 pr-3 text-sm text-ink-700 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700">✕</button>
                )}
              </div>
            </div>

            {/* Tabla con paneles fijos */}
            <div className="overflow-auto rounded-xl border border-border" style={{ maxHeight: 'calc(100vh - 360px)' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-2 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                    <th className="sticky left-0 top-0 z-30 whitespace-nowrap border-b border-r border-ink-100 bg-surface-2 px-4 py-3">Cliente</th>
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">Ingreso {anio1}</th>
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">Ingreso {anio2}</th>
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">Gastos {anio1}</th>
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">Gastos {anio2}</th>
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">Utilidad {anio1}</th>
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">Utilidad {anio2}</th>
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">Var. Utilidad $</th>
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">Var. Utilidad %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {displayed.length === 0 && (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-sm text-ink-400">
                        {search ? `Sin resultados para "${search}"` : 'No hay datos para los filtros seleccionados.'}
                      </td>
                    </tr>
                  )}
                  {displayed.map((r) => {
                    const colU = varColor(r.varUtilidadAbs, 'UTILIDAD');
                    return (
                      <tr key={r.cliente} className="transition-colors hover:bg-surface-2">
                        <td className="sticky left-0 z-10 whitespace-nowrap border-r border-ink-100 bg-inherit px-4 py-2.5 font-medium text-ink-800">
                          {r.cliente}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-ink-600">{money(r.ingresoAnio1)}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono font-semibold text-ink-900">{money(r.ingresoAnio2)}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-ink-600">{money(r.gastosAnio1)}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono font-semibold text-ink-900">{money(r.gastosAnio2)}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-ink-600">{money(r.utilidadAnio1)}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono font-bold text-ink-900">{money(r.utilidadAnio2)}</td>
                        <td className={`whitespace-nowrap px-4 py-2.5 text-right font-mono font-semibold ${colU}`}>
                          {r.varUtilidadAbs >= 0 ? '+' : ''}{money(r.varUtilidadAbs)}
                        </td>
                        <td className={`whitespace-nowrap px-4 py-2.5 text-right font-semibold ${colU}`}>
                          {pctFmt(r.varUtilidadPct)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {displayed.length !== sorted.length && (
              <p className="text-xs text-ink-400">Mostrando {displayed.length} de {sorted.length} clientes.</p>
            )}
          </>
        )}

        {!isLoading && data.length === 0 && (
          <p className="py-8 text-center text-sm text-ink-400">No hay datos para los filtros seleccionados.</p>
        )}
      </div>
    </div>
  );
}
